import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import authService from '../services/authService';

// Estados de autenticación
const AUTH_STATES = {
  IDLE: 'idle',
  LOADING: 'loading', 
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
  ERROR: 'error'
};

// Acciones del reducer
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_AUTHENTICATED: 'SET_AUTHENTICATED', 
  SET_UNAUTHENTICATED: 'SET_UNAUTHENTICATED',
  SET_ERROR: 'SET_ERROR',
  SET_USER_DATA: 'SET_USER_DATA',
  LOGOUT: 'LOGOUT',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Estado inicial
const initialState = {
  status: AUTH_STATES.IDLE,
  user: null,
  token: null,
  error: null,
  isLoading: false
};

// Reducer para manejar el estado de autenticación
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        status: AUTH_STATES.LOADING,
        isLoading: true,
        error: null
      };
    
    case AUTH_ACTIONS.SET_AUTHENTICATED:
      return {
        ...state,
        status: AUTH_STATES.AUTHENTICATED,
        token: action.payload.token,
        user: action.payload.user,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.SET_UNAUTHENTICATED:
      return {
        ...state,
        status: AUTH_STATES.UNAUTHENTICATED,
        user: null,
        token: null,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        status: AUTH_STATES.ERROR,
        error: action.payload,
        isLoading: false
      };
    
    case AUTH_ACTIONS.SET_USER_DATA:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        status: AUTH_STATES.UNAUTHENTICATED
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    default:
      return state;
  }
};

// Crear el contexto con valor por defecto undefined
const NewAuthContext = createContext(undefined);

// Hook personalizado para usar el contexto
export const useNewAuth = () => {
  const context = useContext(NewAuthContext);
  if (context === undefined) {
    throw new Error('useNewAuth debe ser usado dentro de un NewAuthProvider');
  }
  
  // Log para debugging - se puede remover en producción
  if (process.env.NODE_ENV === 'development') {
    console.log('useNewAuth llamado:', {
      isAuthenticated: context.isAuthenticated,
      status: context.status,
      user: context.user ? 'Presente' : 'Ausente',
      token: context.token ? 'Presente' : 'Ausente'
    });
  }
  
  return context;
};

// Componente Provider
export const NewAuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const checkAuthStatus = useCallback(async () => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING });
    
    try {
      // Verificar si hay token y datos del usuario en localStorage
      if (!authService.isAuthenticated()) {
        dispatch({ type: AUTH_ACTIONS.SET_UNAUTHENTICATED });
        return;
      }

      // Verificar token con el servidor
      const result = await authService.me();
      
      if (result.success) {
        const token = authService.getToken();
        const userData = result.data;
        
        dispatch({
          type: AUTH_ACTIONS.SET_AUTHENTICATED,
          payload: { token, user: userData }
        });
      } else {
        // Token inválido o expirado
        await authService.logout();
        dispatch({ type: AUTH_ACTIONS.SET_UNAUTHENTICATED });
      }
    } catch (error) {
      console.error('Error verificando autenticación:', error);
      await authService.logout();
      dispatch({ 
        type: AUTH_ACTIONS.SET_ERROR, 
        payload: 'Error de conexión. Por favor, intenta nuevamente.' 
      });
    }
  }, []);

  // Verificar autenticación al cargar la aplicación
  useEffect(() => {
    const initAuth = async () => {
      await checkAuthStatus();
    };
    initAuth();
  }, [checkAuthStatus]);

  const login = async (email, password) => {
    console.log('Login iniciado en contexto');
    dispatch({ type: AUTH_ACTIONS.SET_LOADING });
    
    try {
      const result = await authService.login({ email, password });
      console.log('Resultado del authService.login:', result);
      
      if (result.success) {
        const { token, user } = result.data;
        console.log('Token obtenido:', token);
        console.log('Usuario obtenido:', user);
        
        dispatch({
          type: AUTH_ACTIONS.SET_AUTHENTICATED,
          payload: { token, user }
        });
        
        console.log('Estado actualizado a AUTHENTICATED');
        return { success: true, message: result.message };
      } else {
        console.log('Login falló:', result.message);
        dispatch({ 
          type: AUTH_ACTIONS.SET_ERROR, 
          payload: result.message || 'Credenciales incorrectas' 
        });
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error en login:', error);
      const errorMsg = 'Error de conexión. Por favor, verifica tu conexión a internet.';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMsg });
      return { success: false, message: errorMsg };
    }
  };

  const logout = async () => {
    // Cerrar la sesión local de inmediato; la llamada al servidor se hace en segundo plano
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error en logout:', error);
    }
  };

  const refreshToken = async () => {
    try {
      const result = await authService.refresh();
      
      if (result.success) {
        const { token } = result.data;
        
        // Actualizar solo el token manteniendo los datos del usuario
        dispatch({
          type: AUTH_ACTIONS.SET_AUTHENTICATED,
          payload: { token, user: state.user }
        });
        
        return { success: true };
      } else {
        await logout();
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error refrescando token:', error);
      await logout();
      return { success: false, message: 'Error refrescando token' };
    }
  };

  const updateUserData = async () => {
    try {
      const result = await authService.me();
      
      if (result.success) {
        dispatch({ 
          type: AUTH_ACTIONS.SET_USER_DATA, 
          payload: result.data 
        });
        return { success: true, data: result.data };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error actualizando datos del usuario:', error);
      return { success: false, message: 'Error actualizando datos del usuario' };
    }
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Función de utilidad para verificar roles
  const hasRole = (role) => {
    return state.user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(state.user?.role);
  };

  // Valores y funciones que se proporcionan a los componentes
  const value = {
    // Estado
    ...state,
    isAuthenticated: state.status === AUTH_STATES.AUTHENTICATED,
    isLoading: state.status === AUTH_STATES.LOADING,
    isUnauthenticated: state.status === AUTH_STATES.UNAUTHENTICATED,
    hasError: state.status === AUTH_STATES.ERROR,
    
    // Funciones
    login,
    logout,
    checkAuthStatus,
    refreshToken,
    updateUserData,
    clearError,
    hasRole,
    hasAnyRole,
    
    // Constantes
    AUTH_STATES
  };

  return <NewAuthContext.Provider value={value}>{children}</NewAuthContext.Provider>;
};

export default NewAuthContext;